import {
  BillingCategory,
  CreateBillingCategoryPayload,
  UpdateBillingCategoryPayload,
} from '../../Models/Rate/RateModels';
import HttpService from './HttpService';
import {
  pathBillingCategories,
  pathBillingCategoryActive,
  pathBillingCategoryId,
} from '../../__settings__/septSoftwarePaths';

interface BillingCategoriesGetResponse {
  successful: boolean;
  data: {
    categories: BillingCategory[];
  };
  sept_tenantid: string;
}

interface BillingCategoryMutateResponse {
  successful: boolean;
  data: {
    message?: string;
    category: BillingCategory;
  };
  sept_tenantid: string;
}

interface BillingCategoryDeleteResponse {
  successful: boolean;
  data: {
    message: string;
  };
  sept_tenantid: string;
}

const http = new HttpService();

class BillingCategoryService {
  /**
   * List the tenant's cost codes. `activeOnly` hides retired codes — pass it for entry surfaces
   * (timesheet grids, create pages); the admin Cost Codes surface omits it to manage retired codes.
   */
  static async getCategories(activeOnly = false): Promise<BillingCategory[]> {
    const path = activeOnly ? `${pathBillingCategories}?active=true` : pathBillingCategories;
    const response = await http.get<BillingCategoriesGetResponse>(path);
    return response.data.categories;
  }

  static async createCategory(payload: CreateBillingCategoryPayload): Promise<BillingCategory> {
    const response = await http.post<BillingCategoryMutateResponse>(pathBillingCategories, payload);
    return response.data.category;
  }

  static async updateCategory(
    categoryId: string,
    payload: UpdateBillingCategoryPayload,
  ): Promise<BillingCategory> {
    const response = await http.put<BillingCategoryMutateResponse>(
      pathBillingCategoryId(categoryId),
      payload,
    );
    return response.data.category;
  }

  /** Retire (active=false) or reactivate (active=true) a code without destroying its history. */
  static async setActive(categoryId: string, active: boolean): Promise<BillingCategory> {
    const response = await http.put<BillingCategoryMutateResponse>(
      pathBillingCategoryActive(categoryId),
      { active },
    );
    return response.data.category;
  }

  /** Hard-delete a code. Reserved codes (e.g. 900) are rejected by the server. */
  static async deleteCategory(categoryId: string): Promise<string> {
    const response = await http.delete<BillingCategoryDeleteResponse>(pathBillingCategoryId(categoryId));
    return response.data.message;
  }
}

export default BillingCategoryService;
